import { GridColDef, GridValidRowModel, DataGridProProps as MuiDataGridProProps } from '@mui/x-data-grid-pro';
import { lighten, darken, alpha } from '@mui/material';
import { Theme } from '..';

// This type is extending from a type defined in @mui/x-data-grid-pro that uses `any`, so we
// also are using any when extending it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableColDefs<RowModel extends GridValidRowModel = any> = GridColDef<RowModel> & {
  // Custom properties for internal processing in chrono-ui. Keep in mind that these are not
  // first-class properties in MUI and should be used very carefully.

  /**
   * Set to `true` to disable the expandable cell behavior for this column. This is useful for
   * columns with custom rendering that does not play well with the expandable cell styles and does
   * not need the expanding behavior (e.g. a cell with a button that always fits in the space).
   */
  disableExpandableCell?: boolean;
};

export type OmittedDataTableProps = 'showCellRightBorder' | 'showColumnRightBorder' | 'components' | 'componentsProps';

// Same as above, MUI uses `any` as the default for `RowModel`, so we're doing the same
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableProps<RowModel extends GridValidRowModel = any> = Omit<
  MuiDataGridProProps<RowModel>,
  OmittedDataTableProps | 'columns'
> & {
  /**
   * Column definitions for the table.
   */
  columns: ReadonlyArray<DataTableColDefs<RowModel>>;

  compact?: boolean;

  /**
   * Use to disable row selection on click when using checkbox selection.
   */
  disableRowSelectionOnClickWithCheckbox?: boolean;

  /**
   * When enabled, the DataTable includes additional styling and column configuration that attempt to wrap and expand
   * cells with long content on hover, so the user can view and copy the full content.
   *
   * You may opt-out of expandable cell behavior on individual columns by setting the custom `disableExpandableCell`
   * property on the column definition to `false` (see `DataTableColDefs`).
   *
   * This feature is opt-in because it is tricky to ensure 100% of use cases will work out-of-the box with custom
   * column rendering and/or table styling (via `sx` or custom class names). When enabling this feature, you **MUST**
   * test your use cases to ensure they behave as expected and no bugs are introduced because of these complexities.
   * If your use of `renderCell` is only modifying the value being returned, consider using `valueGetter` or
   * `valueFormatter` instead, which is simpler and more likely to work out-of-the-box with expandable cells.
   *
   * This feature is currently incompatible with the following configuration:
   * - `getRowHeight` that returns 'auto'. This feature currently only works for consistently sized rows.
   * - `getRowHeight` with dynamic values per row. This feature only works with `getRowHeight` if it returns a consistent
   *   numeric value and only relies on the `densityFactor` from the params.
   */
  enableExpandableCells?: boolean;

  /**
   * Controls pagination when the rowCount is -1 (i.e. unknown/very large).
   *
   * Usually this value is returned by the server when using server-side pagination
   * and indicates if there is another page of data available.
   */
  hasNextPage?: boolean;
};

// This is copied from MUI's source because it is not directly exported and we need these values to replicate the
// behavior when we need to do more complex things with borders.
// https://github.com/mui/mui-x/blob/v6.x/packages/grid/x-data-grid/src/components/containers/GridRootStyles.ts#L8C1-L16C2
// NOTE: this may need to be adjusted when upgrading MUI to newer versions.
export function getDataTableBorderColor(theme: Theme) {
  if (theme.palette.mode === 'light') {
    return lighten(alpha(theme.palette.divider, 1), 0.88);
  }
  return darken(alpha(theme.palette.divider, 1), 0.68);
}

// This is copied from MUI's source because it is not directly exported and we need this value to properly handle some
// styling related to the row height.
// https://github.com/mui/mui-x/blob/v6.x/packages/grid/x-data-grid/src/hooks/features/density/useGridDensity.tsx#L12C1-L12C43
export const DATA_TABLE_COMPACT_DENSITY_FACTOR = 0.7;
